import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RevuesPressService } from '../revues-presse.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

export interface Revue {
  id_article?: number;
  title_ln1: string;
  source_ln1?: string;
  publish_date: string;
  article_url: string;
  snippet?: string;
  press_tags?: string;
}

@Component({
  selector: 'app-revues-form',
  standalone: false,
  templateUrl: './revues-form.component.html',
  styleUrls: ['./revues-form.component.scss']
})
export class RevuesFormComponent implements OnInit, OnDestroy {
  revueId = signal<number | null>(null);
  isLoading = signal(false);
  isFetching = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);

  formData = signal<Revue>({
    title_ln1: '',
    source_ln1: '',
    publish_date: '',
    article_url: '',
    snippet: ''
  });

  private destroy$ = new Subject<void>();

  constructor(
    private revuesPressService: RevuesPressService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.revueId.set(params['id']);
        this.loadRevue(params['id']);
      } else {
        this.revueId.set(null);
        this.resetForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRevue(id: number): void {
    this.isFetching.set(true);
    this.errorMessage.set(null);

    this.revuesPressService.getRevueById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const revue = response.data || response;
          this.formData.set({
            id_article: revue.id_article,
            title_ln1: revue.title_ln1 || '',
            source_ln1: revue.source_ln1 || '',
            publish_date: this.formatDateForInput(revue.publish_date),
            article_url: revue.article_url || '',
            snippet: revue.snippet || '',
            press_tags: revue.press_tags || ''
          });
          this.isFetching.set(false);
        },
        error: (err) => {
          this.isFetching.set(false);
          SwalUtils.error('Erreur', err?.error?.message || 'Impossible de charger cette revue de presse.');
        }
      });
  }

  private resetForm(): void {
    this.formData.set({ title_ln1: '', source_ln1: '', publish_date: '', article_url: '', snippet: '', press_tags: '' });
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  private validateForm(): boolean {
    if (!this.formData().title_ln1.trim()) {
      this.errorMessage.set('Le titre est requis');
      return false;
    }
    if (!this.formData().publish_date) {
      this.errorMessage.set('La date de publication est requise');
      return false;
    }
    if (!this.formData().article_url.trim()) {
      this.errorMessage.set('Le lien article est requis');
      return false;
    }
    try { new URL(this.formData().article_url); } catch {
      this.errorMessage.set('Le lien article doit être une URL valide (https://...)');
      return false;
    }
    return true;
  }

  async onSave(): Promise<void> {
    if (this.isLoading()) return;
    if (!this.validateForm()) return;

    // Lock before the confirm dialog so a double click / Enter can't open or submit twice
    this.isLoading.set(true);

    const isUpdate = this.revueId() !== null;
    const confirmed = await SwalUtils.confirmGreen(
      isUpdate ? 'Modifier la revue de presse' : 'Créer la revue de presse',
      isUpdate
        ? 'Voulez-vous enregistrer les modifications de cette revue de presse ?'
        : 'Voulez-vous créer cette revue de presse ?',
      isUpdate ? 'Modifier' : 'Créer'
    );
    if (!confirmed.isConfirmed) {
      this.isLoading.set(false);
      return;
    }

    this.errorMessage.set(null);
    this.successMessage.set(null);

    const payload = {
      title_ln1: this.formData().title_ln1,
      source_ln1: this.formData().source_ln1 || '',
      publish_date: this.formData().publish_date,
      article_url: this.formData().article_url,
      snippet: this.formData().snippet || '',
      press_tags: this.formData().press_tags || ''
    };

    const req$ = isUpdate
      ? this.revuesPressService.updateRevue(this.revueId()!, payload)
      : this.revuesPressService.createRevue(payload);

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        // stay locked after success: the form is left via navigation, so no second submit is possible
        SwalUtils.success(
          isUpdate ? 'Revue de presse modifiée' : 'Revue de presse créée',
          isUpdate ? 'Les modifications ont été enregistrées avec succès.' : 'La revue de presse a été créée avec succès.'
        ).then(() => this.router.navigate(['/federation/revues-presse']));
      },
      error: (err) => {
        this.isLoading.set(false);
        SwalUtils.error('Erreur', err?.error?.message || 'Une erreur est survenue lors de l\'enregistrement.');
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/federation/revues-presse']);
  }

  updateField(field: keyof Revue, value: any): void {
    this.formData.set({ ...this.formData(), [field]: value });
  }

  private formatDateForInput(dateStr: string): string {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().split('T')[0]; }
    catch { return dateStr; }
  }

  get isEditMode(): boolean { return this.revueId() !== null; }
  get pageTitle(): string { return this.isEditMode ? 'Modifier la revue de presse' : 'Créer une revue de presse'; }
}
